import { rem } from 'polished'
import { Fragment, useCallback, useMemo } from 'react'
import * as React from 'react'
import styled from 'styled-components'

import SmallToggleButton from 'LuxKit/Button/SmallToggleButton'
import Divider from 'LuxKit/Divider'
import MarkdownRender from 'LuxKit/MarkdownRender'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import PriceSummaryRowItemDescription from 'LuxKit/PriceSummaries/PriceSummaryRowItemDescription'
import PriceSummaryRowItemDescriptionDiscount from 'LuxKit/PriceSummaries/PriceSummaryRowItemDescriptionDiscount'
import PriceSummaryRowItemSubtitle from 'LuxKit/PriceSummaries/PriceSummaryRowItemSubtitle'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import useAgentHubCommission from 'agentHub/hooks/useAgentHubCommissionV2'
import FormatCurrency from 'components/Common/FormatCurrency'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Expander from 'components/utils/Expander'
import FormatPercent from 'components/utils/Formatters/FormatPercent'
import Group from 'components/utils/Group'
import config from 'constants/config'
import { OFFER_TYPE_BED_BANK } from 'constants/offer'
import { useAppSelector } from 'hooks/reduxHooks'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import useToggle from 'hooks/useToggle'
import { EmptyArray, sum } from 'lib/array/arrayUtils'
import { getPlural } from 'lib/string/pluralize'
import offerPageURL, { getHotelOfferUrl } from 'lib/offer/offerPageURL'
import { isBedbankOffer } from 'lib/offer/offerTypes'
import { LUXURY_PLUS } from 'luxPlus/constants/base'
import { getIsLuxPlusEnabled } from 'luxPlus/selectors/featureToggle'
import { FormattedMessage, useIntl } from 'react-intl'

import TaxAndFeesText from './TaxAndFeesText'

const PriceDetailsModalExtraDesc = styled.div`
  width: 100%;
  background-color: ${props => props.theme.palette.brand.primary.lightest};
  padding: ${rem(12)};
  margin-top: ${rem(24)};
`

const PaymentSchedule = styled(Group)`
  background-color: ${props => props.theme.palette.neutral.surface.lightBackground};
  padding: ${rem(16)};
`

export type PriceDetailsModalOfferProps = {
  offer: App.Offer | App.BedbankOffer | App.OfferSummary | App.BedbankOfferSummary
  offerId?: undefined
  offerType?: undefined
  saleUnit?: undefined
  displayPricingAsPerPerson?: undefined
} | {
  offer?: undefined
  offerId: string
  offerType: App.AlwaysOnOfferType | App.BedbankOfferType
  saleUnit: string
  displayPricingAsPerPerson: boolean
}

interface ResolvedOfferProps {
  offerId: string
  isBedbank: boolean
  saleUnit: string
  shouldDisplayPricingAsPerPerson: boolean
  finePrintUrl: string
}

function resolveOfferProps({
  offer,
  offerId,
  offerType,
  saleUnit,
  displayPricingAsPerPerson,
}: PriceDetailsModalOfferProps): ResolvedOfferProps {
  if (offer) {
    const isBedbank = isBedbankOffer(offer)
    return {
      offerId: offer.id,
      isBedbank,
      saleUnit: offer.saleUnit,
      shouldDisplayPricingAsPerPerson: isBedbank && offer.displayPricingAsPerPerson,
      finePrintUrl: offerPageURL(offer, undefined, '#fine-print'),
    }
  }
  const isBedbank = offerType === OFFER_TYPE_BED_BANK
  return {
    offerId,
    isBedbank,
    saleUnit,
    shouldDisplayPricingAsPerPerson: isBedbank && displayPricingAsPerPerson,
    // This link will be missing the slug, but the above link is missing the region code, so a
    // redirect is inevitable.
    finePrintUrl: `${getHotelOfferUrl(offerId, offerType)}#fine-print`,
  }
}

type Props = {
  offerPage?: boolean
  duration: number
  taxesAndFees?: number
  propertyFees?: number
  /** The price **without property fees** */
  hotelPrice: number
  /** The member price **without property fees** */
  hotelMemberPrice?: number
  showMemberPrice?: boolean
  flightPrice?: number
  otherFees?: {
    propertyFees: number
    extraGuestsSurcharge: number
  }
  rooms?: Array<App.Occupants>
  extraGuestMessage?: string
  dueAtPropertyMessage?: string
  propertiesFees?: Array<App.PropertyFeesDetails>
  cta?: React.ReactNode
  onCtaAfterClose?: () => void
  hasMobilePromotion?: boolean
  rateId?: string
  discount?: number
  /** When provided with checkOut and rooms, used for commission fetch so modal matches row context (e.g. bedbank option). */
  checkIn?: string
  checkOut?: string
} & PriceDetailsModalOfferProps

function PriceDetailsModal({
  offerPage,
  duration,
  taxesAndFees = 0,
  propertyFees = 0,
  propertiesFees = EmptyArray,
  hotelPrice,
  flightPrice = 0,
  rooms,
  extraGuestMessage,
  dueAtPropertyMessage,
  cta,
  hotelMemberPrice = 0,
  showMemberPrice,
  otherFees,
  hasMobilePromotion,
  rateId,
  discount,
  checkIn,
  checkOut,
  ...offerProps
}: Props) {
  const {
    offerId,
    isBedbank,
    saleUnit,
    shouldDisplayPricingAsPerPerson,
    finePrintUrl,
  } = resolveOfferProps(offerProps)

  const luxPlusEnabled = useAppSelector(getIsLuxPlusEnabled)
  const enabledShowMemberPrice = showMemberPrice && luxPlusEnabled

  const [expanded, expand] = useToggle(false)

  const { resolve } = useModalElementContext<'fine-print' | undefined>()

  const dueAtProperty = (otherFees?.propertyFees || 0) + (otherFees?.extraGuestsSurcharge || 0)
  const priceBeforeTax = hotelPrice - taxesAndFees - dueAtProperty + flightPrice
  const priceAfterTax = hotelPrice + flightPrice
  const memberPriceAfterTax = hotelMemberPrice + flightPrice

  const adultsNumber = useMemo(() => sum(rooms ?? [], room => room.adults) || 2, [rooms])

  const dateOverrides = useMemo(
    () => (checkIn && checkOut && rooms?.length ? { checkIn, checkOut, rooms } : undefined),
    [checkIn, checkOut, rooms],
  )
  const { commissionPercentage, packagesCommission } = useAgentHubCommission(offerId, undefined, dateOverrides)

  const customerView = useAppSelector(state => state.agentHub.customerView)
  const showAgentView = config.agentHub.isEnabled && !customerView && !!commissionPercentage

  const total = priceAfterTax + propertyFees - (discount ?? 0)
  const memberTotal = memberPriceAfterTax + propertyFees - (discount ?? 0)
  const commissionTotal = total * (commissionPercentage / 100)

  const onFinePrint = useCallback(() => resolve('fine-print'), [resolve])

  const intl = useIntl()
  const commissionToDisplay = config.agentHub.isEnabled && isBedbank ? packagesCommission.find(pkg => pkg.packageId === rateId)?.commissionPercentage ?? commissionPercentage : commissionPercentage

  const totalLabel = enabledShowMemberPrice ? (
    <FormattedMessage
      id="zoN1JUjHK_VQnA_J0ziq"
      defaultMessage="Total for non-members"
      description="Label for total price for non-members when member pricing is shown"
    />
  ) : (
    <FormattedMessage
      id="J5s-_en9g7toBJPJzdU4"
      defaultMessage="Total"
      description="Row label for the total price amount displayed in the price breakdown summary view"
    />
  )

  const toggleButtonText = expanded
    ? intl.formatMessage({
        id: 'loK3M5zTQ7XGp4PYrKAS',
        defaultMessage: 'Read Less',
        description: 'Action button label to collapse expanded content in a modal dialog',
      })
    : intl.formatMessage({
        id: '64RRMhT73vpBXiTOT3F1',
        defaultMessage: 'Learn More',
        description: 'Button text to expand and reveal additional content in a collapsible section',
      })

  const durationUnitsLabel = shouldDisplayPricingAsPerPerson
    // eslint-disable-next-line formatjs/no-multiple-plurals
    ? intl.formatMessage(
        {
          id: 'bYXGx55RlXeGvF2Ocx1V',
          defaultMessage: '{nights, plural, one {# night} other {# nights}} {persons, plural, one {# person} other {# persons}}',
          description: 'Price details label with night count and person count',
        },
        { nights: duration, persons: adultsNumber },
      )
    // eslint-disable-next-line formatjs/no-multiple-plurals
    : intl.formatMessage(
        {
          id: 'eBXUHuo0mnmk6r-S-v2b',
          defaultMessage: '{nights, plural, one {# night} other {# nights}} {units, plural, one {# {saleUnit}} other {# {pluralSaleUnit}}}',
          description: 'Price details label with night count and room/unit count',
        },
        { nights: duration, units: rooms?.length || 1, saleUnit, pluralSaleUnit: getPlural(saleUnit) },
      )

  return <ModalBase size="L">
    <ModalHeader
      title={intl.formatMessage({
        id: 'mNJ3xsTScg81J9R-vZrC',
        defaultMessage: 'Price details',
        description: 'Modal title for the price details breakdown dialog showing cost components',
      })}
    />
    <ModalBody>
      <ModalContent>
        <Group direction="vertical" gap={12}>
          <div>
            <PriceSummaryRowItemSubtitle
              label={durationUnitsLabel}
              value={priceBeforeTax}
              valueFormat="roundedDollar"
            />
            {duration > 1 && <PriceSummaryRowItemDescription
              label={intl.formatMessage({
                id: 'TtyF7sPkhXl2wsa60Nsg',
                defaultMessage: 'Average per night',
                description: 'Label for average price per night in the pricing or cost breakdown section',
              })}
              value={priceBeforeTax / duration}
              valueFormat="roundedDollar"
            />}
          </div>
          {discount && <>
            <Divider direction="horizontal" kind="secondary" />
            <PriceSummaryRowItemDescriptionDiscount
              label={intl.formatMessage({
                id: 'NzjNyKhhfVz4eWz6WMlR',
                defaultMessage: 'Promo Code',
                description: 'Row label for the promotional code discount amount in the price breakdown',
              })}
              discountValue={discount}
              discountValueFormat="roundedDollar"
            />
          </>}
          {hasMobilePromotion && <>
            <Divider direction="horizontal" kind="secondary" />
            <VerticalSpacer gap={4}>
              <PriceSummaryRowItemDescriptionDiscount
                label={intl.formatMessage({
                  id: 'BpJpekfipehkMQswYpe-',
                  defaultMessage: 'Mobile only price',
                  description: 'Label for mobile-only promotional price for promotional codes or vouchers',
                })}
              />
              <PriceSummaryRowItemDescription
                label={intl.formatMessage({
                  id: 'rVtqSIaCIPk1JyAsqWXS',
                  defaultMessage: 'You’re getting a discounted price only available on mobile and tablet devices',
                  description: 'Description for mobile-only promotional price in a modal dialog',
                })}
              />
            </VerticalSpacer>
          </>}
          {taxesAndFees > 0 && <>
            <Divider direction="horizontal" kind="secondary" />
            <VerticalSpacer gap={4}>
              <PriceSummaryRowItemDescription
                label={intl.formatMessage({
                  id: 'e8f4qek__H_UfAd56RC5',
                  defaultMessage: 'Taxes and fees',
                  description: 'Row label for the taxes and fees amount in the order price breakdown summary',
                })}
                value={taxesAndFees}
                valueFormat="roundedDollar"
              />
              <Expander expanded={expanded}>
                <TaxAndFeesText
                  isBedbank={isBedbank}
                  offerPage={!!offerPage}
                  finePrintUrl={finePrintUrl}
                  onFinePrint={onFinePrint}
                />
              </Expander>
              <SmallToggleButton onClick={expand} on={expanded} size="small">
                {toggleButtonText}
              </SmallToggleButton>
            </VerticalSpacer>
          </>}
          {!otherFees && propertyFees > 0 && propertiesFees.length === 0 && <>
            <Divider direction="horizontal" kind="secondary" />
            <PriceSummaryRowItemSubtitle
              label={intl.formatMessage({
                id: 'peXREuj99hBvVjdjKhLE',
                defaultMessage: 'Due at property',
                description: 'Form field label for fees due at property in a modal dialog',
              })}
              value={propertyFees}
              valueFormat="roundedDollar"
            />
          </>}
          {!otherFees && propertiesFees.length > 0 && <>
            <Divider direction="horizontal" kind="secondary" />
            <div>
              {propertiesFees.map((fee) => <Fragment key={fee.propertyName}>
                <PriceSummaryRowItemSubtitle
                  label={intl.formatMessage({
                    id: 'oZvaRYDyE1FqFgfEE-ng',
                    defaultMessage: 'Local tax',
                    description: 'Row label for the local tax amount in the order price breakdown summary display',
                  })}
                  value={fee.propertyFees}
                  valueFormat="roundedDollar"
                />
                <PriceSummaryRowItemDescription label={intl.formatMessage(
                  {
                    id: 'Cfj5hfDGTxSFHo-p6oHs',
                    defaultMessage: '(Payable at {propertyName})',
                    description: 'Label indicating where local tax is payable in a modal dialog',
                  },
                  { propertyName: fee.propertyName },
                )}
                />
              </Fragment>)}
            </div>
          </>}
          <Divider direction="horizontal" kind="secondary" />
          {showAgentView && <>
            <PriceSummaryRowItemSubtitle
              label={
                <FormattedMessage
                  id="ABWKVfhpTK6wHVRaOUjw"
                  defaultMessage="Total commission (<percentage></percentage>)"
                  description="Form field label for total commission with percentage"
                  values={{
                    percentage: () => <FormatPercent key="percent" value={commissionToDisplay / 100} />,
                  }}
                />
              }
              value={commissionTotal * -1}
              valueFormat="roundedDollar"
            />
            <PriceSummaryRowItemSubtitle
              label={intl.formatMessage({
                id: 'cp1YQ_JdTWpBESFFsC5U',
                defaultMessage: 'Gross total',
                description: 'Row label for the gross total amount in the price breakdown summary',
              })}
              value={total}
              valueFormat="roundedDollar"
            />
            <PriceSummaryRowItemSubtitle
              label={intl.formatMessage({
                id: 'pw0vADlvi5eIVYEERh34',
                defaultMessage: 'Net total',
                description: 'Row label for the net total amount in the order price breakdown summary display',
              })}
              value={total - commissionTotal}
              valueFormat="roundedDollar"
            />
          </>}
          {!showAgentView && <>
            <PriceSummaryRowItemSubtitle
              label={totalLabel}
              value={total}
              valueFormat="roundedDollar"
            />
            {enabledShowMemberPrice && <>
              <Divider direction="horizontal" kind="secondary" />
              <PriceSummaryRowItemDescriptionDiscount
                label={intl.formatMessage(
                  {
                    id: 'mNYlB9c1yuSNLv6qIt93',
                    defaultMessage: '{programName} member discount',
                    description: 'Row label for the loyalty program member discount in the price breakdown summary',
                  },
                  { programName: LUXURY_PLUS.PROGRAM_NAME },
                )}
                discountValue={memberPriceAfterTax - priceAfterTax}
                discountValueFormat="roundedDollar"
              />
              <Divider direction="horizontal" kind="secondary" />
              <PriceSummaryRowItemSubtitle
                label={intl.formatMessage(
                  {
                    id: 'zOMczCX1-cwBeJfWdOJB',
                    defaultMessage: 'Total for {programName} members',
                    description: 'Label for total price for members in the pricing or cost breakdown section',
                  },
                  { programName: LUXURY_PLUS.PROGRAM_NAME },
                )}
                value={memberTotal}
                valueFormat="roundedDollar"
              />
            </>}
          </>}
          <Divider direction="horizontal" kind="secondary" />
          {dueAtProperty > 0 &&
            <PaymentSchedule direction="vertical" gap={8}>
              <Group direction="horizontal" horizontalAlign="space-between">
                <Heading variant="heading6" colour="highlight-secondary">
                  {intl.formatMessage({
                    id: 'jZuu9Wg58C37ztYwEkWf',
                    defaultMessage: 'Due on checkout',
                    description: 'Heading for amount due on checkout during the checkout and payment process',
                  })}
                </Heading>
                <Heading variant="heading6" colour="highlight-secondary" align="end">
                  <FormatCurrency value={total - dueAtProperty} format="preciseDollar" />
                </Heading>
              </Group>
              <Group direction="horizontal" horizontalAlign="space-between" className="dense">
                <BodyText variant="medium" weight="normal">
                  {intl.formatMessage({
                    id: 'Z-1-5O1k_VOVD42Fl3tu',
                    defaultMessage: 'Due at property on arrival',
                    description: 'Form field label for amount due at property on arrival',
                  })}
                </BodyText>
                <BodyText variant="medium" weight="normal" align="end">
                  <FormatCurrency value={dueAtProperty} format="preciseDollar" />
                </BodyText>
              </Group>
            </PaymentSchedule>}
          {extraGuestMessage && <PriceDetailsModalExtraDesc>
            <BodyText variant="large" weight="bold">
              {intl.formatMessage({
                id: '5Inn6iKIKGU1G09W464g',
                defaultMessage: 'Extra guest surcharges',
                description: 'Section heading for extra guest surcharges section',
              })}
            </BodyText>
            <MarkdownRender content={extraGuestMessage} />
          </PriceDetailsModalExtraDesc>}
          {dueAtPropertyMessage && <PriceDetailsModalExtraDesc>
            <BodyText variant="large" weight="bold">
              {intl.formatMessage({
                id: 'U3PdrXYAV7PX3PEWZNaT',
                defaultMessage: 'Due at property',
                description: 'Section heading for due at property section in a modal dialog',
              })}
            </BodyText>
            <MarkdownRender content={dueAtPropertyMessage} />
          </PriceDetailsModalExtraDesc>}
        </Group>
      </ModalContent>
    </ModalBody>
    {!!cta && <ModalFooter>
      <div onClick={() => resolve(undefined)}>
        {cta}
      </div>
    </ModalFooter>}
  </ModalBase>
}

export default PriceDetailsModal
